import _ from 'lodash'
import {MutationTree} from 'vuex';
import {UserState} from '@/modules/user/store/index';
import Pagination from '@/modules/core/model/pagination';
import User from '@/modules/user/model/User';
import Vue from 'vue'
import Session from '@/modules/user/model/Session';
import UserGroup from '@/modules/user/model/UserGroup';

export enum MutationTypes {
    SetUsersPagination = 'SET_USERS_PAGINATION',
    SetCurrentUser = 'SET_CURRENT_USER',
    ResetCurrentUser = 'RESET_CURRENT_USER',
    UpdateUserField = 'UPDATE_USER_FIELD',
    SetUserAttribute = 'SET_USER_ATTRIBUTE',
    DeleteUserAttribute = 'DELETE_USER_ATTRIBUTE',
    ClearUserAttributes = 'CLEAR_USER_ATTRIBUTES',
    SetNamedPagination = 'SET_NAMED_PAGINATION',
    // Mutaciones para sesiones de usuario
    SetSessionPagination = 'SET_SESSION_PAGINATION',
    RemoveGroupFromCurrentUser = 'REMOVE_GROUP_FROM_CURRENT_USER',
    ToggleCheckingEmailAvailability = 'TOGGLE_CHECKING_EMAIL_AVAILABILITY',
    // Mutaciones para grupos de usuarios
    SetUserGroups = 'SET_USER_GROUPS',
    ToggleUserGroupsFetched = 'TOGGLE_USER_GROUP_FETCHED',
    AddUserGroup = 'ADD_USER_GROUP',
    DeleteUserGroup = 'DELETE_USER_GROUP',
    SetCurrentUserGroup = 'SET_CURRENT_USER_GROUP',
    SetSelectedGroupUsers = 'SET_SELECTED_GROUP_USERS',
    AddSelectedGroupUser = 'ADD_SELECTED_GROUP_USER',
    DeletedSelectedGroupUser = 'DELETE_SELECTED_GROUP_USER',
    UpdateUserGroupField = 'UPDATE_USER_GROUP_FIELD',
    RemoveGroupFromCurrentGroup = 'REMOVE_GROUP_FROM_CURRENT_GROUP',
    ToggleCheckingiDeleteFromBBDDUnigest = 'TOGGLE_USER_GROUP_DELETE_PLAFORM_UNIGEST',
    SetTasksBeforeDeleteUser = 'SET_TASK_BEFORE_DELETE_USER',
    DeleteTasksBeforeDeleteUser = 'DELETE_TASK_BEFORE_DELETE_USER',
    SetTasksBeforeDeleteUserGroup = 'SET_TASK_BEFORE_DELETE_USER_GROUP',
    DeleteTasksBeforeDeleteUserGroup = 'DELETE_TASK_BEFORE_DELETE_USER_GROUP',
    SetValidateGroupYesNotBeParent = 'SET_VALIDATE_GROUP_YES_NOT_BE_PARENT',
}

const mutations: MutationTree<UserState> = {
    /**
     * Establece la paginación que se mostrará al usuario
     * @param state
     * @param pagination
     */
    [MutationTypes.SetUsersPagination]: (state, pagination: Pagination<User>) => {
        state.usersPagination = pagination
    },

    /**
     * Establece el usuario actualmente en edición
     *
     * @param state
     * @param user
     */
    [MutationTypes.SetCurrentUser]: (state, user: User) => state.currentUser = user,

    /**
     * Cambio el estado de la propiedad que indica si la aplicación esta comprobado la disponibilidad
     * del correo electrónico introducido en la ficha del usuario
     *
     * @param state
     * @param checking
     */
    [MutationTypes.ToggleCheckingEmailAvailability]: (state, checking: boolean) => {
        state.checkingEmailAvailability = checking
    },

    /**
     * Actualiza un campo del usuario actualmente en edición por el usuario
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateUserField]: (state, payload: {field: string, value: any}) => {
        _.set(state.currentUser, payload.field, payload.value)
    },

    /**
     * Registra un atributo en la lista de atributos del usuario
     *
     * @param state
     * @param attribute
     */
    [MutationTypes.SetUserAttribute]: (state, attribute: {key: string, value: any}) => {
        const { key, value } = attribute
        Vue.set(state.currentUser.attributes, key, value)
    },

    [MutationTypes.DeleteUserAttribute]: (state, key: string) => {
        Vue.delete(state.currentUser.attributes, key)
    },

    [MutationTypes.ClearUserAttributes]: (state) => Vue.set(state.currentUser, 'attributes', {}),

    /**
     * Establece la paginación de sesiones mostrada al usuario actualmente
     *
     * @param state
     * @param p
     */
    [MutationTypes.SetSessionPagination]: (state, p: Pagination<Session>) => state.sessionsPagination = p,
    [MutationTypes.ResetCurrentUser]: (state) => state.currentUser = new User(),

    /**
     * Establece la lista de grupos de usuarios mostrada al usuario
     *
     * @param state
     * @param groups
     */
    [MutationTypes.SetUserGroups]: (state, groups: UserGroup[]) => state.userGroups = groups,

    /**
     * Establece la bandera que indica si la aplicación ya ha obtenido la lista
     * de grupos de usuarios
     *
     * @param state
     * @param fetched
     */
    [MutationTypes.ToggleUserGroupsFetched]: (state, fetched: boolean) => state.userGroupsFetched = fetched,

    /**
     * Añade un grupo de usuarios a la lista ya existente. Si ya existe un grupo
     * con el mismo, este será reemplazado por la versión insertada
     *
     * @param state
     * @param group
     */
    [MutationTypes.AddUserGroup]: (state, group: UserGroup) => {
        const index = state.userGroups.findIndex(g => g.id === group.id)
        index === -1
            ? state.userGroups.push(group)
            : state.userGroups.splice(index, 1, group)
    },

    /**
     * Elimina un grupo de usuarios de la lista
     *
     * @param state
     * @param group
     */
    [MutationTypes.DeleteUserGroup]: (state, id: number) => {
        const index = state.userGroups.findIndex(g => g.id === id)
        if (index !== -1) {
            state.userGroups.splice(index, 1)
        }
    },

    /**
     * Establece el grupo de usuario actualmente seleccionado por el usuario
     *
     * @param state
     * @param group
     */
    [MutationTypes.SetCurrentUserGroup]: (state, group: UserGroup) => state.userGroup = group,

    [MutationTypes.SetSelectedGroupUsers]: (state, users: User[]) => {
        state.selectedGroupUsers = users
    },

    /**
     * Actualiza un campo del grupo de usuarios actualmente seleccionado
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateUserGroupField]: (state, payload: {field: string, value: any}) => {
        _.set(state.userGroup, payload.field, payload.value)
    },

    /**
     * Registra una paginación nombrada
     *
     * @param state
     * @param payload
     */
    [MutationTypes.SetNamedPagination]: (state, payload: {name: string, pagination: Pagination<User>}) => {
        const {name, pagination} = payload
        if (!name) {
            throw new Error('El nombre de la paginación no puede estar vacío')
        }

        state.namedUserPaginations[name] = pagination
    },

    [MutationTypes.RemoveGroupFromCurrentUser]: (state, group: number) => {
        const { currentUser } = state

        const index = currentUser.groups.findIndex(g => g.id === group)
        index !== -1 && currentUser.groups.splice(index, 1)
    },

    [MutationTypes.RemoveGroupFromCurrentGroup]: (state, group: number) => {
        const { userGroup } =  state
        const index = userGroup.children.findIndex(g => g.id === group)
        index !== -1 && userGroup.children.splice(index, 1)
    },

    [MutationTypes.ToggleCheckingiDeleteFromBBDDUnigest]: (state, checking: boolean) => {
        state.checkingiDeleteFromBBDDUnigest = checking
    },


    [MutationTypes.SetTasksBeforeDeleteUser]: (state, payload: {name: string, task: () => Promise<any>}) => {
        const {name, task} = payload
        if (!name) {
            throw new Error('El nombre de la tarea no puede estar vacío')
        }
        state.tasksBeforeDeleteUser.set(name, task);
    },

    [MutationTypes.DeleteTasksBeforeDeleteUser]: (state, payload: {name: string}) => {
        const {name} = payload
        if (!name) {
            throw new Error('El nombre de la tarea no puede estar vacío')
        }
        state.tasksBeforeDeleteUser.delete(name);
    },

    [MutationTypes.SetTasksBeforeDeleteUserGroup]: (state, payload: {name: string, task: () => Promise<any>}) => {
        const {name, task} = payload
        if (!name) {
            throw new Error('El nombre de la tarea no puede estar vacío')
        }
        state.tasksBeforeDeleteUserGroup.set(name, task);
    },

    [MutationTypes.DeleteTasksBeforeDeleteUserGroup]: (state, payload: {name: string}) => {
        const {name} = payload
        if (!name) {
            throw new Error('El nombre de la tarea no puede estar vacío')
        }
        state.tasksBeforeDeleteUserGroup.delete(name);
    },

    [MutationTypes.SetValidateGroupYesNotBeParent]: (state, value: number) => {
        state.validateGroupYesNotBeParent = value
    },

}

export default mutations

